import React, { useState, useRef } from 'react';
import { processOCRText } from '../utils/ocrProcessor';

const GOOGLE_CLOUD_VISION_API_KEY = process.env.REACT_APP_GOOGLE_CLOUD_VISION_API_KEY;
const API_KEY = process.env.REACT_APP_CATALOGUE_API_KEY;
const Vision = () => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ocrResults, setOcrResults] = useState(null);
  const [ocrText, setOcrText] = useState(null);
  const [searchResults, setSearchResults] = useState(null);

  const fileInputRef = useRef(null);
  const takePhotoInputRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setOcrResults(null);
      setSearchResults(null);
    setError(null);
      setOcrText(null);
    }
  };

  // Add API key validation at the start
  const processImage = async () => {
    if (!GOOGLE_CLOUD_VISION_API_KEY || !API_KEY) {
      setError('API keys not configured properly');
      return;
    }

    if (!imageFile) {
      setError('Please select an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = await getBase64(imageFile);
      
      const visionResponse = await fetch(`https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_CLOUD_VISION_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [{
            image: {
              content: base64Image.split(',')[1]
            },
            features: [{
              type: 'TEXT_DETECTION'
            }]
          }]
        })
      });

      if (!visionResponse.ok) {
        const errorText = await visionResponse.text();
        console.error('Google Vision API error response:', errorText);
        throw new Error(`Google Vision API error: ${visionResponse.status} ${visionResponse.statusText}`);
      }

      const visionData = await visionResponse.json();
      console.debug('Google Vision API response:', visionData);

      if (visionData.error) {
        console.error('Google Vision API returned error:', visionData.error);
        throw new Error(visionData.error.message);
      }

      const fullText = visionData.responses[0]?.fullTextAnnotation?.text || visionData.responses[0]?.textAnnotations?.[0]?.description || '';
      console.debug('Full OCR text from Google Vision:', fullText);
      setOcrText(fullText);

      const { cardName, cardNumber } = processOCRText(fullText);
      
      setOcrResults({ cardName, cardNumber });

      if (cardName || cardNumber) {
        await searchCard(cardName, cardNumber);
      } else {
        setError('Could not detect card name or number from the image');
        }

    } catch (err) {
      console.error('Error processing image:', err);
      setError(`Failed to process image: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const searchCard = async (cardName, cardNumber) => {
    try {
      const trimmedName = cardName ? cardName.trim() : '';
      const trimmedNumber = cardNumber ? cardNumber.trim() : '';

      if (trimmedName && trimmedNumber) {
        const slugGuess = `${trimmedName.toLowerCase().replace(/\s+/g, '-')}-${trimmedNumber.toLowerCase().replace('/', '-')}-holo`;
        console.log('Vision: Attempting exact match with slug:', slugGuess);

        const lookupResponse = await fetch('https://pokepulse-catalogue-service.replit.app/api/cards/lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
          body: JSON.stringify({
            slugs: [slugGuess]
          })
      });

        const lookupData = await lookupResponse.json();
        console.log('Vision: Lookup response:', lookupData);
      
        if (lookupData.cards && lookupData.cards.length > 0) {
          setSearchResults(lookupData.cards);
          return;
      } else {
          console.log('Vision: No exact match found, falling back to search');
      }
    }

      const searchParams = {
        query: trimmedName || '',
        card_number: trimmedNumber || '',
        exact_match: true,
        page: 1,
        limit: 10
      };

      console.log('Vision: Sending search params:', searchParams);

      const searchResponse = await fetch('https://pokepulse-catalogue-service.replit.app/api/cards/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(searchParams)
      });

      const searchData = await searchResponse.json();
      console.log('Vision: Search response:', searchData);

      if (searchData.cards && searchData.cards.length > 0) {
        const filteredCards = searchData.cards.filter(card => {
          if (trimmedNumber && card.card_number.toLowerCase() !== trimmedNumber.toLowerCase()) {
            return false;
          }
          if (trimmedName && !card.card_name.toLowerCase().includes(trimmedName.toLowerCase())) {
            return false;
          }
          return true;
        });

        console.log('Vision: Filtered to', filteredCards.length, 'results');

        if (filteredCards.length > 0) {
          setSearchResults(filteredCards);
        } else {
          setError('No cards found matching the detected text');
        }
      } else {
        setError('No cards found matching the detected text');
      }
    } catch (err) {
      console.error('Vision: Search error:', err);
      setError('Failed to search for card. Please try again.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Scan</h1>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={takePhotoInputRef}
              onChange={handleImageUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Select Image
          </button>
            <button
              onClick={() => takePhotoInputRef.current.click()}
              className="mb-4 ml-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Take Photo
            </button>
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Card preview"
                  className="max-w-sm mx-auto rounded-lg"
                />
              </div>
                        )}
                      </div>
          <button
            onClick={processImage}
            disabled={loading || !imageFile}
            className="w-full bg-black hover:bg-gray-800 text-white px-4 py-2 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Scan Image'}
          </button>
          {error && (
            <div className="text-red-600 mt-4">
              {error}
            </div>
          )}
          {ocrText && (
            <div className="mt-4 p-4 bg-gray-100 rounded max-w-md">
              <h3 className="font-semibold mb-2">Detected OCR Text:</h3>
              <pre className="whitespace-pre-wrap break-words">
                {ocrText}
              </pre>
            </div>
          )}
          {ocrResults && (
            <div className="mt-4 p-4 bg-gray-100 rounded max-w-md">
              <h3 className="font-semibold mb-2">Detected Name and Number:</h3>
              <p>Card Name: {ocrResults.cardName || 'Not detected'}</p>
              <p>Card Number: {ocrResults.cardNumber || 'Not detected'}</p>
            </div>
          )}
          {searchResults && (
            <div className="mt-6">
              <h2 className="text-xl font-semibold mb-4">Search Results</h2>
              <div className="space-y-4">
                {searchResults.map((card) => (
                  <div key={card.id} className="border rounded-lg p-4">
                    <div className="flex items-start">
                      {card.thumbnail_url && (
                        <img
                          src={card.thumbnail_url}
                          alt={card.card_name}
                          className="w-24 h-auto rounded-lg mr-4"
                        />
                      )}
                      <div>
                        <h3 className="font-bold">{card.card_name}</h3>
                        <p className="text-sm text-gray-600">Number: {card.card_number}</p>
                        <p className="text-sm text-gray-600">Rarity: {card.rarity}</p>
                        <p className="text-sm text-gray-600">Set: {card.set_id}</p>
                        {card.material && (
                          <p className="text-sm text-gray-600">Material: {card.material}</p>
                        )}
                        {card.ebay_url && (
                          <a 
                            href={card.ebay_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm block mt-2"
                          >
                            View on eBay
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Vision;